import { createContext, useContext, useState, ReactNode } from 'react';
import { VideoPlayerCamera } from 'types';

type FullscreenVideoPlayerContextType = {
  isOpen: boolean;
  camera: VideoPlayerCamera | null;
  openFullscreen: (camera: VideoPlayerCamera) => void;
  closeFullscreen: () => void;
};

const FullscreenVideoPlayerContext = createContext<FullscreenVideoPlayerContextType | undefined>(
  undefined
);

/**
 * Provides global fullscreen video player state and controls.
 *
 * This context enables any component to trigger or close fullscreen video playback.
 * It holds the active camera object and manages mounting/unmounting of the fullscreen player UI.
 *
 * Use `openFullscreen(camera)` to initiate fullscreen mode, and `closeFullscreen()` to exit.
 */
export const FullscreenVideoPlayerProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [camera, setCamera] = useState<VideoPlayerCamera | null>(null);

  const openFullscreen = (cam: VideoPlayerCamera) => {
    setCamera(cam);
    setIsOpen(true);
  };

  const closeFullscreen = () => {
    setIsOpen(false);
    setCamera(null);
  };

  return (
    <FullscreenVideoPlayerContext.Provider
      value={{ isOpen, camera, openFullscreen, closeFullscreen }}
    >
      {children}
    </FullscreenVideoPlayerContext.Provider>
  );
};

export const useFullscreenVideoPlayer = () => {
  const context = useContext(FullscreenVideoPlayerContext);
  if (!context) {
    throw new Error('useFullscreenVideoPlayer must be used within FullscreenVideoPlayerProvider');
  }
  return context;
};
